import { AiReportType, AreasItemType } from '../../../api/types/apiGetReport';
import Alert from '../../../components/Alert/Alert';
import cn from '../../../lib/classNames';

import Disclaimer from './Disclaimer/Disclaimer';
import hospitalImg from './images/hospital.svg';
import safetyImg from './images/safety.svg';
import unsafetyImg from './images/unsafety.svg';
import LocationMap from './Locations/LocationMap';
import Locations from './Locations/Locations';
import SummaryRiskBrief from './SummaryRiskBrief/SummaryRiskBrief';
import AdvisoryAlert from './AdvisoryAlert';
import Emergency from './Emergency';
import FailedToGenerateWarningReport from './FailedToGenerateWarningReport';
import SectionTitle from './SectionTitle';

import styles from './RiskBriefReport.module.scss';
import { hasErrorField } from './utils';

type ListItemType = AreasItemType & {
	number?: string;
	address?: string;
	backgroundImage?: string;
};

type RiskBriefReportType = {
	data: AiReportType;
	showWarningMessage?: boolean;
	destination: string;
};

const TITLE_SAFEST_PLACES = 'Safest places';
const TITLE_HIGH_RISK_AREAS = 'High Risk areas';

const hasError = (data: AiReportType) =>
	hasErrorField(data.areas) ||
	hasErrorField(data.locations) ||
	hasErrorField(data.summary) ||
	hasErrorField(data.advisories);

export const MAP_GROUP = [
	{
		icon: '',
		btnText: 'All',
		field: 'all',
		backgroundImage: '',
		getList: () => [],
		helpMessage: () => '',
	},
	{
		icon: 'dangerous',
		btnText: TITLE_HIGH_RISK_AREAS,
		field: 'high_risk_locations',
		backgroundImage: unsafetyImg,
		getList: (list: AiReportType) =>
			hasErrorField(list.areas) ? [] : list.areas.high_risk_locations,
		helpMessage: (data: AiReportType) =>
			hasErrorField(data.areas) ? '' : data.areas.help_message_risk,
	},
	{
		icon: 'encrypted',
		btnText: TITLE_SAFEST_PLACES,
		field: 'safest_areas',
		backgroundImage: safetyImg,
		getList: (list: AiReportType) => (hasErrorField(list.areas) ? [] : list.areas.safest_areas),
		helpMessage: (data: AiReportType) =>
			hasErrorField(data.areas) ? '' : data.areas.help_message_safest || '',
	},
	{
		icon: 'emergency',
		btnText: 'Hospitals',
		field: 'hospitals',
		backgroundImage: hospitalImg,
		getList: (list: AiReportType) => list?.hospitals,
		helpMessage: () => '',
	},
];

export default function RiskBriefReport({
	data,
	showWarningMessage = false,
	destination,
}: RiskBriefReportType) {
	const addTypeMarkers = () => {
		MAP_GROUP.forEach((e) => {
			if (e.field !== 'all') {
				if (e.getList && e.getList(data).length) {
					e.getList(data).forEach((item: ListItemType) => {
						item.backgroundImage = e.backgroundImage;
					});
				}
			}
		});
	};

	addTypeMarkers();

	const DEFAULT_DESTINATION_TEXT = 'the destination';
	const warningMessage = `Travel to ${destination || DEFAULT_DESTINATION_TEXT
		} is currently subject to significant risks due to active conflict. Information on the safest, most hazardous areas, and critical locations is unreliable and continuously evolving. Stay informed with our latest updates and consult local official sources. Exercise extreme caution.`;

	return (
		<section
			className={cn(styles['risk-safety-summary-container'], 'risk-safety-summary-container')}
		>
			<div className={styles.content}>
				{hasError(data) && <FailedToGenerateWarningReport />}
				<AdvisoryAlert advisories={data?.advisories} />
				<SectionTitle
					title="Travel destination risk brief"
					subTitle="Ranking according to the Violent Crime Risk (ViCRI) Index for cities and countries"
				/>
				<SummaryRiskBrief summary={data.summary} />
				<SectionTitle
					title="Critical Numbers"
					subTitle="Including Local Emergency and Law Enforcement Information"
				/>
				<Emergency
					className={cn(styles.section, styles['section-widget-wrap'])}
					data={data.locations}
				/>
				<SectionTitle
					title="Significant Locations"
					subTitle="Including Selected Hospitals, Safe Areas, High-Risk Neighborhoods, etc."
				/>
				{showWarningMessage && <Alert type="warning" icon="warning_amber" text={warningMessage} />}
				<LocationMap className={cn(styles.section, styles['section-widget-wrap'])} data={data} />
				<Locations data={data} />
				<SectionTitle title="Important Notice to Users:" />
				<Disclaimer classNameTerms={cn(styles.section, styles['section-widget-wrap'])} />
			</div>
		</section>
	);
}
